import { PropsWithChildren, useMemo } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePageChange } from 'src/modules/common/hooks/usePageChange.ts';
import { BELIMO_HASH_PREFIX } from 'src/BELIMO_HASH_PREFIX.ts';
import {
    ProjectNavigation,
    ProjectNavigationContext,
} from 'src/projects/navigation/ProjectNavigationContext.ts';

/**
 * Implements ProjectNavigation on top of react-router. Used by the routed apps
 * (valves/actuators/sensors) so the ProjectPage keeps its original URL-driven behaviour:
 * the pagination page lives in the `:page` route param and navigation mutates the hash URL.
 */
export const RouterProjectNavigationProvider = ({ children }: PropsWithChildren) => {
    const { page } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const currentPage = useMemo(() => {
        const parsedPage = parseInt(page || '1', 10);
        return parsedPage < 1 ? 1 : parsedPage;
    }, [page]);

    const [, onPageChange] = usePageChange(
        (newPage: number) => location.pathname.replace(/\/projects\/\d+/, `/projects/${newPage}`),
        (newSearchParams) => newSearchParams,
        () => location.state,
    );

    const value = useMemo<ProjectNavigation>(() => ({
        currentPage,
        goToPage: (newPage: number) => onPageChange(newPage, searchParams),
        goBack: () => navigate({
            ...location,
            search: location.search,
            pathname: location.pathname.replace(/\/projects\/\d+/, ''),
        }, {
            state: location.state,
            replace: true,
        }),
        goHome: () => navigate(`${BELIMO_HASH_PREFIX}/`),
        onNavigate: (url: string) => {
            window.location.assign(url);
        },
    }), [currentPage, onPageChange, searchParams, navigate, location]);

    return (
        <ProjectNavigationContext.Provider value={value}>
            {children}
        </ProjectNavigationContext.Provider>
    );
};
